import { useMemo } from 'react';
import styled from 'styled-components';

import { breakpoints, media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { useAppRouter } from '@/shared/hooks/app/useAppRouter';
import { useBreakpoints } from '@/shared/hooks/useBreakpoints';
import { BFC } from '@/shared/types';
import { Icon } from '@/shared/uikit/Icon';
import { Button } from '@/shared/uikitV2/Button';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals';

import { useTasks } from '../hooks/useTasks';

import { TasksList } from './TasksList';
import { TasksTable } from './TasksTable';
import { WidgetContainer } from './WidgetContainer';

type TaskManagerProps = {
  caseId?: string;
};

export const TaskManager: BFC<TaskManagerProps> = ({ caseId }) => {
  const { t } = useTranslation('features/vfo', { keyPrefix: 'taskManager' });

  const router = useAppRouter();
  const { tasksCreatedByMe, tasksAssignedToMe } = useTasks();
  const { sm } = useBreakpoints();
  const modals = useModalsStore();

  const filteredTasks = useMemo(() => {
    if (!caseId) {
      return {
        tasksAssignedToMe,
        tasksCreatedByMe,
      };
    }

    return {
      tasksAssignedToMe: tasksAssignedToMe.filter((item) => item.caseId === caseId),
      tasksCreatedByMe: tasksCreatedByMe.filter((item) => item.caseId === caseId),
    };
  }, [caseId, tasksAssignedToMe, tasksCreatedByMe]);

  const hasTasks = Boolean(
    filteredTasks.tasksAssignedToMe.length || filteredTasks.tasksCreatedByMe.length,
  );

  return (
    <WidgetContainerStyled>
      <Header>
        <TitleWrapper>
          <Title>{t('title')}</Title>
        </TitleWrapper>
        {hasTasks && (
          <LinkStyled
            type="text"
            size="middle"
            onClick={() => {
              router.push(caseId ? `/vfo/case/${caseId}/tasks` : '/vfo/tasks', undefined, {
                shallow: true,
              });
            }}
          >
            {t('viewAll')}
          </LinkStyled>
        )}
      </Header>
      {!hasTasks ? (
        <EmptyContainer>
          <Icon name="list-checks" size="s44" />
          <NoTasks>{t(caseId ? 'noTasksCase' : 'noTasks')}</NoTasks>
          <Button
            type="default"
            size="middle"
            onClick={() => {
              modals.openModal({
                type: 'AddNewTaskModal',
                caseId,
              });
            }}
          >
            {t(caseId ? 'addBtnCase' : 'addBtn')}
          </Button>
        </EmptyContainer>
      ) : sm ? (
        <TasksList showAllTab={false} />
      ) : (
        <Tables>
          <TableWrapper>
            <TasksTable
              minWidth={330}
              title={t('assignedToMe')}
              tasks={filteredTasks.tasksAssignedToMe}
              showAssignee={false}
              rowsClassName="rows"
              small
            />
          </TableWrapper>
          <TableWrapper>
            <TasksTable
              minWidth={390}
              title={t('createdByMe')}
              tasks={filteredTasks.tasksCreatedByMe}
              rowsClassName="rows"
              small
            />
          </TableWrapper>
        </Tables>
      )}
    </WidgetContainerStyled>
  );
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  ${media.md}, ${breakpoints.sm} {
    padding: 0 16px 0 16px;
    margin-bottom: 0;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
`;

const Title = styled.span`
  font-weight: 600;
`;

const LinkStyled = styled(Button)`
  &&& {
    color: ${COLORS.systemSky200};
    font-weight: 700;
    &:hover {
      color: ${COLORS.systemSky200};
    }
  }
`;

const Tables = styled.div`
  display: flex;
  align-items: stretch;
  gap: 24px;

  ${media.md}, ${breakpoints.sm} {
    flex-direction: column;

    .rows {
      max-height: 255px;
    }
  }
`;

const TableWrapper = styled.div`
  max-height: 336px;
  min-height: 180px;
  flex: 1;
  overflow: auto;
`;

const WidgetContainerStyled = styled(WidgetContainer)`
  ${media.md}, ${breakpoints.sm} {
    padding: 16px 0 0 0;
  }
`;

const EmptyContainer = styled.div`
  color: ${COLORS.systemDark100};
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 12px;

  ${media.md}, ${breakpoints.sm} {
    padding-bottom: 16px;
  }
`;

const NoTasks = styled.span`
  color: ${COLORS.systemDark200};
  font-weight: 700;
  margin-bottom: 12px;
`;
